import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, from, of } from 'rxjs';
import { takeUntil, finalize, switchMap, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';

import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { HttpResponse } from '@angular/common/http';
import { ChannelIntegrationService } from '@app/api/channel-service/api/channelIntegration.service';
import { AllShopsProductQueryDTO } from '@app/api/channel-service/model/allShopsProductQueryDTO';
import { OnlineSkuWithShopVO } from '@app/api/channel-service/model/onlineSkuWithShopVO';
import { PageInfoOnlineSkuWithShopVO } from '@app/api/channel-service/model/pageInfoOnlineSkuWithShopVO';
import { formatCurrencyVND } from '@shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  canOpenLazadaProductListing,
  isLazadaChannelCode,
} from '@app/feature/center-business/channel/utils/lazada-integration.util';
import { isShopeeChannelCode } from '@app/feature/center-business/channel/utils/shopee-integration.util';

export interface MarketplaceProductRow {
  id: string;
  shopId?: number;
  category: string;
  productCode: string;
  productName: string;
  skuCode: string;
  skuName: string;
  channelName: string;
  channelType: string;
  salePrice: number;
  promotionPrice: number;
  availableStock: number;
  stockNum: number;
  lazadaQuantity: number;
  salesPrice: number;
  status: string;
}

@Component({
  selector: 'app-third-party-goods',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TagModule,
    TooltipModule,
    ToastModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  templateUrl: './third-party-goods.component.html',
  styleUrl: './third-party-goods.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThirdPartyGoodsComponent implements OnInit, OnDestroy {
  /** Template: Lazada-only row actions (normalized channel type). */
  protected readonly isLazadaChannelCode = isLazadaChannelCode;
  protected readonly isShopeeChannelCode = isShopeeChannelCode;

  private readonly destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly channelIntegration = inject(ChannelIntegrationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  loading = signal(false);
  items = signal<MarketplaceProductRow[]>([]);
  totalRecords = signal(0);
  currentPage = signal(0);
  pageSize = signal(20);
  first = signal(0);
  searchParams = signal<Record<string, unknown>>({});

  showProductDeleteDialog = signal(false);
  productDeleteDialogTitle = signal('');
  productDeleteDialogMessage = signal('');
  productDeleteDialogButtons = signal<ConfirmationButton[]>([]);
  productDeleteDialogData = signal<{ productId: string } | null>(null);

  searchFormat: FormFieldConfig[] = [
    {
      key: 'spuCode',
      label: 'third_party.goods.search.fields.product_code',
      type: 'text',
      placeholder: 'third_party.goods.search.placeholders.enter_product_code',
    },
    {
      key: 'spuName',
      label: 'third_party.goods.search.fields.product_name',
      type: 'text',
      placeholder: 'third_party.goods.search.placeholders.enter_product_name',
    },
    {
      key: 'skuCode',
      label: 'third_party.goods.search.fields.sku_code',
      type: 'text',
      placeholder: 'third_party.goods.search.placeholders.enter_sku_code',
    },
    {
      key: 'skuName',
      label: 'third_party.goods.search.fields.sku_name',
      type: 'text',
      placeholder: 'third_party.goods.search.placeholders.enter_sku_name',
    },
    {
      key: 'channelName',
      label: 'third_party.goods.search.fields.channel_name',
      type: 'text',
      placeholder: 'third_party.goods.search.placeholders.enter_channel_name',
    },
    {
      key: 'channelType',
      label: 'third_party.goods.search.fields.channel_type',
      type: 'text',
      placeholder: 'third_party.goods.search.placeholders.enter_channel_type',
    },
  ];

  // Permission flags - using menu codes: third-party.third-party_products.view/edit/delete
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('third-party.third-party_products.view');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('third-party.third-party_products.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'third-party.third-party_products.delete',
    );
  });

  // Check if user has any action permissions
  hasAnyActionPermission = computed(
    () => this.canRead() || this.canUpdate() || this.canDelete(),
  );

  defaultColumns = computed((): TableColumn[] => {
    const cols: TableColumn[] = [
      {
        label: 'onlineStore.form.product_list.table.columns.category',
        prop: 'category',
        width: 150,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.product_code',
        prop: 'productCode',
        width: 120,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.product_name',
        prop: 'productName',
        width: 200,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sku_code',
        prop: 'skuCode',
        width: 120,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sku_name',
        prop: 'skuName',
        width: 150,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'third_party.goods.table.columns.channel_name',
        prop: 'channelName',
        width: 140,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'third_party.goods.table.columns.channel_type',
        prop: 'channelType',
        width: 120,
        align: 'left',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.available_stock',
        prop: 'availableStock',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        headerTooltip: 'inventory_lookup.table.columns.available_tooltip',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.current_stock',
        prop: 'stockNum',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        headerTooltip: 'inventory_lookup.table.columns.on_hand_tooltip',
      },
      {
        label: 'third_party.goods.table.columns.platform_quantity',
        prop: 'lazadaQuantity',
        width: 140,
        align: 'right',
        headerAlign: 'center',
      },
      {
        label: 'onlineStore.form.product_list.table.columns.sales_price',
        prop: 'salesPrice',
        width: 120,
        align: 'right',
        headerAlign: 'center',
        type: 'custom',
        template: 'currencyTemplate',
      },
    ];

    // Conditionally add actions column if user has any action permissions
    if (this.hasAnyActionPermission()) {
      cols.push({
        label: 'onlineStore.form.product_list.table.columns.actions',
        prop: 'actions',
        width: 280,
        align: 'center',
        headerAlign: 'center',
        type: 'actions',
      });
    }

    return cols;
  });

  ngOnInit(): void {
    this.loadItems();
    // Subscribe to permissions loaded to trigger change detection
    this.permissionService.permissionsLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildQuery(): AllShopsProductQueryDTO {
    const params = this.searchParams();
    const dto: AllShopsProductQueryDTO = {
      page: this.currentPage(),
      size: this.pageSize(),
    };
    if (typeof params['spuCode'] === 'string')
      dto.spuCode = params['spuCode'].trim();
    if (typeof params['spuName'] === 'string')
      dto.spuName = params['spuName'].trim();
    if (typeof params['skuCode'] === 'string')
      dto.skuCode = params['skuCode'].trim();
    if (typeof params['skuName'] === 'string')
      dto.skuName = params['skuName'].trim();
    if (typeof params['channelName'] === 'string')
      dto.channelName = params['channelName'].trim();
    if (typeof params['channelType'] === 'string')
      dto.channelType = params['channelType'].trim();
    return dto;
  }

  private mapRow(vo: OnlineSkuWithShopVO): MarketplaceProductRow {
    const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
    const num = (v: unknown) =>
      typeof v === 'number' && !Number.isNaN(v) ? v : 0;
    const stock = num(vo.stockNum);
    // Available stock is computed by IMS (Stock Service), not re-derived here from raw fields.
    const availableStock = num(vo.available);
    return {
      // vo.id is a number; str() only keeps strings → would yield '' → Number('')=0 → /product/0.
      id: vo.id != null ? String(vo.id) : '',
      shopId: vo.shopId ?? undefined,
      category: str(vo.categoryName),
      productCode: str(vo.spuCode),
      productName: str(vo.spuName),
      skuCode: str(vo.skuCode),
      skuName: str(vo.skuName),
      channelName: str(vo.channelName),
      channelType: str(vo.channelType ?? vo.channelTypeCode),
      salePrice: num(vo.marketPrice ?? vo.salesPrice),
      promotionPrice: num(vo.activityPrice),
      availableStock,
      stockNum: stock,
      lazadaQuantity: num(vo.lazadaQuantity),
      salesPrice: num(vo.salesPrice ?? vo.marketPrice),
      status: str(vo.status) || 'DRAFT',
    };
  }

  private extractTotal(body: unknown, list: MarketplaceProductRow[]): number {
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      return (body as PageInfoOnlineSkuWithShopVO).totalElements ?? list.length;
    }
    if (Array.isArray(body)) {
      return body.length;
    }
    return 0;
  }

  loadItems(): void {
    this.loading.set(true);
    const dto = this.buildQuery();

    this.channelIntegration
      .getAllShopsPublishedProducts(dto, 'response')
      .pipe(
        switchMap(
          (response: HttpResponse<PageInfoOnlineSkuWithShopVO | Blob>) => {
            const body = response?.body;
            if (body instanceof Blob && body.type?.includes('json')) {
              return from(body.text()).pipe(
                map((text) => JSON.parse(text) as PageInfoOnlineSkuWithShopVO),
              );
            }
            return of((body ?? {}) as PageInfoOnlineSkuWithShopVO);
          },
        ),
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (body: PageInfoOnlineSkuWithShopVO) => {
          const content =
            body?.pageContent ?? (Array.isArray(body) ? body : null) ?? [];
          const list = Array.isArray(content)
            ? content.map((vo: OnlineSkuWithShopVO) => this.mapRow(vo))
            : [];
          const total = this.extractTotal(body, list);
          this.items.set([...list]);
          this.totalRecords.set(Number(total) || 0);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.warn('[ThirdPartyGoods] all-shops API error:', err);
          this.items.set([]);
          this.totalRecords.set(0);
          this.cdr.markForCheck();
        },
      });
  }

  onSearch(data: SearchFormData): void {
    this.searchParams.set({ ...data });
    this.currentPage.set(0);
    this.first.set(0);
    this.loadItems();
  }

  onReset(): void {
    this.searchParams.set({});
    this.currentPage.set(0);
    this.first.set(0);
    this.pageSize.set(20);
    this.loadItems();
  }

  onPageChange(event: { page?: number; first?: number; rows?: number }): void {
    const rows = event.rows ?? 20;
    const page =
      event.page ??
      (typeof event.first === 'number' ? Math.floor(event.first / rows) : 0);
    this.currentPage.set(page);
    this.first.set(page * rows);
    this.pageSize.set(rows);
    this.loadItems();
  }

  formatCurrency(value: unknown): string {
    return formatCurrencyVND(Number(value) || 0);
  }

  getStatusDisplayText(status: string): string {
    if (!status) return '';
    let key: string;
    switch (status) {
      case 'DRAFT':
        key = 'onlineStore.form.product_list.status.draft';
        break;
      case 'ACTIVE':
        key = 'onlineStore.form.product_list.status.active';
        break;
      default:
        key = 'onlineStore.form.product_list.status.inactive';
        break;
    }
    return this.translateService.instant(key);
  }

  getStatusSeverity(status: string): 'warning' | 'success' | 'danger' | 'info' {
    if (!status) return 'warning';
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'danger';
      case 'INACTIVE':
        return 'danger';
      default:
        return 'warning';
    }
  }

  getStatusStyle(status: string): Record<string, string> {
    if (!status) return {};
    switch (status) {
      case 'ACTIVE':
        return { '--status-color': '#22c55e' };
      case 'DRAFT':
        return { '--status-color': '#ef4444' };
      case 'INACTIVE':
        return { '--status-color': '#6b7280' };
      default:
        return { '--status-color': '#eab308' };
    }
  }

  /** Open product detail in view mode (Lazada or Shopee). */
  onView(row: MarketplaceProductRow): void {
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    if (row.shopId == null || (!isLazadaChannelCode(row.channelType) && !isShopeeChannelCode(row.channelType))) return;
    this.router.navigate(
      [`/channel/online-store/${row.shopId}/product-listing/view/${row.id}`],
      {
        state: { returnToMarketplaceProducts: true },
      },
    );
  }

  /** Open product edit (Lazada or Shopee). */
  onEdit(row: MarketplaceProductRow): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    if (row.shopId == null || (!isLazadaChannelCode(row.channelType) && !isShopeeChannelCode(row.channelType))) return;
    this.router.navigate(
      [`/channel/online-store/${row.shopId}/product-listing/edit/${row.id}`],
      {
        state: {
          returnToMarketplaceProducts: true,
          availableStock: row.availableStock,
        },
      },
    );
  }

  /** Navigate to product edit (for DRAFT – activate = save and publish from edit page). */
  onActivate(row: MarketplaceProductRow): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.onEdit(row);
  }

  /** Show delete confirmation (Lazada or Shopee); label depends on status (deactivate vs delete). */
  onDelete(row: MarketplaceProductRow): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    if (!isLazadaChannelCode(row.channelType) && !isShopeeChannelCode(row.channelType)) return;
    this.productDeleteDialogTitle.set(
      this.translateService.instant(
        'onlineStore.form.product_list.dialog.delete_title',
      ),
    );
    this.productDeleteDialogMessage.set(
      this.translateService.instant(
        'onlineStore.form.product_list.messages.confirm_delete',
      ),
    );
    const confirmLabel =
      row.status === 'ACTIVE'
        ? this.translateService.instant(
            'onlineStore.form.product_list.actions.deactivate_and_delete',
          )
        : this.translateService.instant('common.actions.delete');
    this.productDeleteDialogButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      { label: confirmLabel, action: 'confirm', severity: 'danger' },
    ]);
    this.productDeleteDialogData.set({ productId: row.id });
    this.showProductDeleteDialog.set(true);
  }

  onProductDeleteDialogAction(action: string): void {
    this.showProductDeleteDialog.set(false);
    if (action === 'confirm') {
      const data = this.productDeleteDialogData();
      if (data) this.executeProductDelete(data.productId);
    }
    this.productDeleteDialogData.set(null);
  }

  private executeProductDelete(productId: string): void {
    const id = Number(productId);
    if (Number.isNaN(id)) return;
    this.channelIntegration
      .deleteOnlineSku(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'onlineStore.form.product_list.messages.delete_success',
            ),
          });
          this.loadItems();
        },
        error: (err: any) => {
          const msg =
            err?.error?.message ??
            err?.error ??
            this.translateService.instant(
              'onlineStore.form.product_list.messages.delete_error',
            );
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail:
              typeof msg === 'string'
                ? msg
                : this.translateService.instant(
                    'onlineStore.form.product_list.messages.delete_error',
                  ),
          });
        },
      });
  }
}
